import { UserProfile } from "../../store/slices/user_profile.slice"
import { DuetHeapEvents, SierraHeapEvents } from "../../types/heap.type"
import {
  ArticleData,
  CustomHeapEvent,
  CustomHeapUserData,
  EventName,
  getLocationAndNameByUrlPath
} from "./event_attributes"

const trackEvent = (heapEvent: CustomHeapEvent) => {
  if (typeof window !== "undefined") {
    if (window?.heap) {
      if (process.env.DEBUG === "true") {
        console.log("trackEvent(): ", { heapEvent })
      }

      window?.heap.track(heapEvent?.EventName, heapEvent)
    }
  }
}

const trackUser = (heapEvent: CustomHeapUserData) => {
  if (typeof window !== "undefined") {
    if (window?.heap) {
      window?.heap.track(heapEvent?.EventName, heapEvent)
      if (heapEvent?.userId) {
        window?.heap?.identify(heapEvent?.userId)
      }
    }
  }
}

export const events = {
  duetEvent: (eventName: DuetHeapEvents) => {
    const locAndName = getLocationAndNameByUrlPath()

    const event: CustomHeapEvent = {
      EventLocation: locAndName?.EventLocation,
      EventName: eventName,
      PageArea: "fixed-bottom",
      PropertyValue: "Duet ChatBot",
      Value: "Duet ChatBot Popup",
      Component: "ChatBot"
    }

    trackEvent(event)
  },
  sierraEvent: (eventName: SierraHeapEvents) => {
    const locAndName = getLocationAndNameByUrlPath()

    const event: CustomHeapEvent = {
      EventLocation: locAndName?.EventLocation,
      EventName: eventName,
      PageArea: "fixed-bottom",
      PropertyValue: "Sierra ChatBot",
      Value: "Sierra ChatBot Modal",
      Component: "ChatBot"
    }

    trackEvent(event)
  },
  homepageHeaderClickEvent: (componentName: string) => {
    const locAndName = getLocationAndNameByUrlPath()

    const event: CustomHeapEvent = {
      EventLocation: locAndName?.EventLocation,
      EventName: locAndName?.EventName,
      PageArea: "header",
      PropertyValue: "Image",
      Value: "Header Logo",
      Component: componentName
    }

    trackEvent(event)
  },
  loginEvent: (componentName: string) => {
    const locAndName = getLocationAndNameByUrlPath()

    const event: CustomHeapEvent = {
      EventLocation: locAndName?.EventLocation,
      EventName: locAndName?.EventName,
      PageArea: "header",
      PropertyValue: "Login",
      Value: "Profile",
      Component: componentName
    }

    trackEvent(event)
  },
  logoutEvent: (componentName: string) => {
    const locAndName = getLocationAndNameByUrlPath()

    const event: CustomHeapEvent = {
      EventLocation: locAndName?.EventLocation,
      EventName: locAndName?.EventName,
      PageArea: "header",
      PropertyValue: "Logout",
      Value: "Profile",
      Component: componentName
    }

    trackEvent(event)
  },
  handleHomepageTabClick: (propertyValue: string, componentName: string) => {
    const locAndName = getLocationAndNameByUrlPath()

    const event: CustomHeapEvent = {
      EventLocation: locAndName?.EventLocation,
      EventName: locAndName?.EventName,
      PageArea: "body",
      PropertyValue: propertyValue,
      Value: "Tab",
      Component: componentName
    }

    trackEvent(event)
  },
  homepageFeaturedArticleClick: (
    category: string,
    articleName: string,
    componentName: string
  ) => {
    const locAndName = getLocationAndNameByUrlPath()

    const event: CustomHeapEvent = {
      EventLocation: locAndName?.EventLocation,
      EventName: locAndName?.EventName,
      PageArea: "body",
      PropertyValue: articleName,
      Value: `Featured Articles - ${category}`,
      Component: componentName
    }

    trackEvent(event)
  },
  homepageBrowseByTopic: (category: string, componentName: string) => {
    const locAndName = getLocationAndNameByUrlPath()

    const event: CustomHeapEvent = {
      EventLocation: locAndName?.EventLocation,
      EventName: locAndName?.EventName,
      PageArea: "body",
      PropertyValue: category,
      Value: `Browse By Topic - ${category}`,
      Component: componentName
    }

    trackEvent(event)
  },
  layoutContactUsButtonClick: (componentName: string) => {
    const locAndName = getLocationAndNameByUrlPath()

    const event: CustomHeapEvent = {
      EventLocation: locAndName?.EventLocation,
      EventName: locAndName?.EventName,
      PageArea: "body",
      PropertyValue: "Contact Us",
      Value: "Button",
      Component: componentName
    }

    trackEvent(event)
  },
  articlePageData: (
    componentName: EventName,
    articleDataObject: ArticleData
  ) => {
    const locAndName = getLocationAndNameByUrlPath()
    const event: CustomHeapEvent = {
      EventLocation: locAndName?.EventLocation,
      EventName: componentName,
      PageArea: "body",
      PropertyValue: "Article Id",
      Value: articleDataObject?.articleId,
      Component: componentName,
      requiresAssistance: articleDataObject?.requiresAssistance,
      helpArticleAssistanceCTAs: articleDataObject?.helpArticleAssistanceCTAs,
      helpArticleCategory: articleDataObject?.helpArticleCategory,
      helpArticleSubcategory: articleDataObject?.helpArticleSubcategory
    }

    trackEvent(event)
  },
  userProfileData: (eventName: EventName, userProfile: UserProfile) => {
    const locAndName = getLocationAndNameByUrlPath()
    const event: CustomHeapUserData = {
      EventLocation: locAndName?.EventLocation,
      EventName: eventName,
      orgId: userProfile?.organizationId,
      assortmentPlan: userProfile?.assortmentPlan,
      isFree: userProfile?.isFree,
      isHighVAlue: userProfile?.isHighValue,
      isNewAccount: userProfile?.isNewAccount,
      isOrganizer: userProfile?.isOrganizer,
      userId: userProfile?.userData?.id
    }
    trackUser(event)
  },
  footerLinkClick: (footerLinkText: string, componentName: string) => {
    const locAndName = getLocationAndNameByUrlPath()

    const event: CustomHeapEvent = {
      EventLocation: locAndName?.EventLocation,
      EventName: locAndName?.EventName,
      PageArea: "footer",
      PropertyValue: footerLinkText,
      Value: "Footer Link",
      Component: componentName
    }

    trackEvent(event)
  },
  footerLanguageSelector: (langCode: string, componentName: string) => {
    const locAndName = getLocationAndNameByUrlPath()

    const event: CustomHeapEvent = {
      EventLocation: locAndName?.EventLocation,
      EventName: locAndName?.EventName,
      PageArea: "footer",
      PropertyValue: langCode,
      Value: "Language Selector",
      Component: componentName
    }

    trackEvent(event)
  },
  browseHelpCenterButton404: (btnText: string, componentName: string) => {
    const locAndName = getLocationAndNameByUrlPath()

    const event: CustomHeapEvent = {
      EventLocation: locAndName?.EventLocation,
      EventName: locAndName?.EventName,
      PageArea: "body",
      PropertyValue: btnText,
      Value: "Button",
      Component: componentName
    }

    trackEvent(event)
  },
  breadcrumbClick: (pageTitleClicked: string, componentName: string) => {
    const locAndName = getLocationAndNameByUrlPath()

    const event: CustomHeapEvent = {
      EventLocation: locAndName?.EventLocation,
      EventName: locAndName?.EventName,
      PageArea: "body",
      PropertyValue: pageTitleClicked,
      Value: "Breadcrumb",
      Component: componentName
    }

    trackEvent(event)
  },
  relatedArticleClick: (pageTitleClicked: string, componentName: string) => {
    const locAndName = getLocationAndNameByUrlPath()

    const event: CustomHeapEvent = {
      EventLocation: locAndName?.EventLocation,
      EventName: locAndName?.EventName,
      PageArea: "body",
      PropertyValue: pageTitleClicked,
      Value: "Breadcrumb",
      Component: componentName
    }

    trackEvent(event)
  },
  bannerClick: (callToAction: string, componentName: string) => {
    const locAndName = getLocationAndNameByUrlPath()

    const event: CustomHeapEvent = {
      EventLocation: locAndName?.EventLocation,
      EventName: locAndName?.EventName,
      PageArea: "body",
      PropertyValue: callToAction,
      Value: "Banner",
      Component: componentName
    }

    trackEvent(event)
  },
  scrollDepth: (scrollDepthPercent: number) => {
    const event: CustomHeapEvent = {
      EventLocation: "All",
      EventName: "Scroll Depth",
      PropertyValue: "Scroll Position",
      Value: scrollDepthPercent,
      PageArea: "body",
      Component: "Layout",
      scrollDepthPercent: scrollDepthPercent
    }

    trackEvent(event)
  },
  findMyTicketsError: (errorString: string) => {
    const event: CustomHeapEvent = {
      EventLocation: "All",
      EventName: "DUET_ORDER_LOOKUP_ERROR",
      Value: errorString,
      Component: "ChatBot",
      PageArea: "chatbot",
      PropertyValue: "chatbot"
    }

    trackEvent(event)
  },
  userHasEventWithin24Hours: (
    eventIsWithin24Hours: boolean,
    isAttendee: boolean,
    componentName: string
  ) => {
    const event: CustomHeapEvent = {
      EventLocation: "All",
      EventName: "Has Event Within 24 Hours",
      Value: `${eventIsWithin24Hours}`,
      PropertyValue: `${eventIsWithin24Hours}`,
      Component: componentName,
      PageArea: "body",
      eventIsWithin24Hours,
      isAttendee
    }

    trackEvent(event)
  }
}
